/**
 * Lines feature module (W2-07 lines-panel; DESIGN.md §4.10, §4.21, §5.5; WORKPACKAGES Appendix B: `lines.*`).
 *
 * Provides:
 *  - the "Lines" dock tab (chart.dock);
 *  - dialogs `DIALOG.lineDelete`, `DIALOG.parentPrompt`, `DIALOG.lineType` (modal);
 *  - commands `lines.*` with keys in both presets (RPE preset: Ctrl+wheel line switch with nothing selected; RPE Tab,
 *    the preview's line info readout, is W2-02's `preview.toggleReadout` binding);
 *  - the `#` palette provider (`#12`, `#main`);
 *  - a status-bar item: the active line chip (opens the switcher) and the host of the feature's own dialogs;
 *  - the setting `ext.lines.view` (tree or export order) and help topics.
 */

import { DIALOG } from '../../app/dialogs/types';
import type { FeatureModule, HelpTopicDef, SettingDef } from '../../app/registry/types';
import { LINE_COMMANDS, LINE_KEYBINDINGS, LINES_PANEL_ID, linesPaletteProvider } from './lineCommands';
import { LineDeleteDialog } from './LineDeleteDialog';
import { m } from './lines.messages';
import { LinesHost } from './LinesHost';
import { LinesPanel } from './LinesPanel';
import { LineTypeDialog } from './LineTypeDialog';
import { ParentPromptDialog } from './ParentPromptDialog';

export const LINES_SETTINGS: readonly SettingDef[] = [
  {
    path: 'ext.lines.view',
    title: m.settingView,
    description: m.settingViewHelp,
    level: 'guided',
    kind: 'enum',
    options: [
      { value: 'tree', label: m.viewTree },
      { value: 'order', label: m.viewOrder },
    ],
  },
];

export const LINES_HELP: readonly HelpTopicDef[] = [
  {
    id: 'lines.panel',
    title: m.helpPanelTitle,
    body: m.helpPanelBody,
    showMe: { panel: LINES_PANEL_ID },
    level: 'guided',
  },
  {
    id: 'lines.parents',
    title: m.helpParentsTitle,
    body: m.helpParentsBody,
    showMe: { panel: LINES_PANEL_ID },
    level: 'guided',
  },
  { id: 'lines.types', title: m.helpTypesTitle, body: m.helpTypesBody, level: 'guided' },
];

const linesModule = {
  id: 'lines',
  commands: LINE_COMMANDS,
  keybindings: LINE_KEYBINDINGS,
  panels: [
    {
      id: LINES_PANEL_ID,
      title: m.panelTitle,
      icon: 'ListTree',
      slot: 'chart.dock',
      order: 10,
      level: 'guided',
      component: LinesPanel,
    },
  ],
  dialogs: [
    { id: DIALOG.lineDelete, kind: 'modal', component: LineDeleteDialog },
    { id: DIALOG.parentPrompt, kind: 'modal', component: ParentPromptDialog },
    { id: DIALOG.lineType, kind: 'modal', component: LineTypeDialog },
  ],
  statusItems: [{ id: 'lines.active', slot: 'statusbar', order: 10, component: LinesHost }],
  paletteProviders: (s) => [linesPaletteProvider(s)],
  settings: LINES_SETTINGS,
  helpTopics: LINES_HELP,
} satisfies FeatureModule;

export default linesModule;
